import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";

export default function Nav({ settings }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const orderUrl = settings?.order_url || "#";
  const phone = settings?.phone || "(732) 928-9115";
  const telHref = `tel:${phone.replace(/[^0-9+]/g, "")}`;

  const links = [
    { href: "#menu", label: "Menu" },
    { href: "#gallery", label: "Gallery" },
    { href: "#about", label: "Our Story" },
    { href: "#visit", label: "Visit" },
  ];

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color] duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F9F6F0]/85 border-b border-[var(--line)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-home-link"
          className="flex items-center gap-3"
        >
          {settings?.logo_url ? (
            <img
              src={settings.logo_url}
              alt="Emilio's Restaurant & Pizzeria"
              className="h-11 md:h-12 w-auto rounded-md object-contain"
            />
          ) : (
            <img
              src="/logo-emilios.jpg"
              alt="Emilio's Restaurant & Pizzeria"
              className="h-11 md:h-12 w-auto rounded-md object-contain shadow-sm"
            />
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium tracking-wide hover:text-[var(--terracotta)] transition-colors ${
                scrolled ? "text-[var(--charcoal)]" : "text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={telHref}
            data-testid="nav-call-btn"
            className={`inline-flex items-center gap-2 text-sm font-medium ${
              scrolled ? "text-[var(--charcoal)]" : "text-white"
            } hover:text-[var(--terracotta)] transition-colors`}
          >
            <Phone size={16} />
            {phone}
          </a>
          <a
            href={orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-order-btn"
            className="inline-flex items-center px-5 py-2.5 rounded-full bg-[var(--terracotta)] text-white text-sm font-semibold hover:bg-[var(--terracotta-hover)] transition-all hover:-translate-y-0.5"
          >
            Order Now
          </a>
        </div>

        <button
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className={`md:hidden p-2 rounded-full ${
            scrolled ? "text-[var(--charcoal)]" : "text-white"
          }`}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[var(--dough)] border-t border-[var(--line)] px-5 py-4 space-y-3">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className="block text-[var(--charcoal)] font-medium py-1"
            >
              {l.label}
            </a>
          ))}
          <a
            href={telHref}
            data-testid="mobile-nav-call"
            className="block text-[var(--charcoal)] font-medium py-1"
          >
            {phone}
          </a>
          <a
            href={orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-nav-order"
            className="block text-center mt-2 px-5 py-3 rounded-full bg-[var(--terracotta)] text-white font-semibold"
          >
            Order Now
          </a>
        </div>
      )}
    </header>
  );
}
